import React, { useEffect, useRef, useState } from "react"

// providers
import { trans } from "@/_providers/Translation"

// helpers
import Api from "@/_helpers/Api"

// librararies
import moment from "moment"
import Skeleton from "react-loading-skeleton"
import { Gauge } from "@mui/x-charts"

const HOURS = [
  "00:00",
  "01:00",
  "02:00",
  "03:00",
  "04:00",
  "05:00",
  "06:00",
  "07:00",
  "08:00",
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
  "18:00",
  "19:00",
  "20:00",
  "21:00",
  "22:00",
  "23:00",
]

const PX_PER_MINUTE = 2

function BusyTechnicians() {
  const getTimeLeft = () => {
    const now = new Date()
    // COMPENSATION е ширината на първата колона (списъка с техници) + коефициент, който да компенсиора ширината на колоните с часовете
    const COMPENSATION = 209
    return (
      COMPENSATION +
      (now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60) *
        PX_PER_MINUTE
    )
  }

  const [currentTimeLeft, setCurrentTimeLeft] = useState(getTimeLeft)
  const [currentTime, setCurrentTime] = useState(moment().format("HH:mm"))
  const [loading, setLoading] = useState(true)
  const [busyTechnicians, setBusyTechnicians] = useState([])
  const [freeTechnicians, setFreeTechnicians] = useState([])
  const [extraChartData, setExtraChartData] = useState({})

  const refreshTimeoutRef = useRef(null)
  const scheduleRef = useRef(null)
  const tableRef = useRef(null)
  const [tableHeight, setTableHeight] = useState(0)

  useEffect(() => {
    let interval

    const tick = () => {
      setCurrentTimeLeft(getTimeLeft())
      setCurrentTime(moment().format("HH:mm"))
    }

    const now = new Date()
    const msUntilNextMinute =
      (60 - now.getSeconds()) * 1000 - now.getMilliseconds()

    const timeout = setTimeout(() => {
      tick()
      interval = setInterval(tick, 60000)
    }, msUntilNextMinute)

    return () => {
      clearTimeout(timeout)
      if (interval) clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current)

    const now = new Date()
    const candidates = []

    busyTechnicians.forEach((tech) => {
      ;(tech?.reservations || [])?.forEach((res) => {
        const end = new Date(res.end_date.replace(" ", "T"))
        if (end > now) candidates.push(end.getTime())
      })
    })

    freeTechnicians.forEach((tech) => {
      ;(tech?.reservations || [])?.forEach((res) => {
        const start = new Date(res.start_date.replace(" ", "T"))
        if (start > now) candidates.push(start.getTime())
      })
    })

    if (candidates.length > 0) {
      const delay = Math.min(...candidates) - now.getTime()
      refreshTimeoutRef.current = setTimeout(() => loadData(), delay)
    }

    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current)
    }
  }, [busyTechnicians, freeTechnicians])

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (!loading && scheduleRef.current) {
      scheduleRef.current.scrollLeft = currentTimeLeft - 230
    }
  }, [loading])

  useEffect(() => {
    if (tableRef.current) {
      setTableHeight(tableRef.current.offsetHeight)
    }
  }, [busyTechnicians, freeTechnicians, loading])

  function loadData() {
    Api.get("analytics/technicians-availability")
      .then((res) => {
        setBusyTechnicians(res.data.busy_technicians || [])
        setFreeTechnicians(res.data.free_technicians || [])
        setExtraChartData({
          total: res.data.total_count || 0,
          free: res.data.free_count || 0,
          busy: res.data.busy_count || 0,
          busyPercent: res.data.busy_percentage,
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  function getShortName(fullName) {
    if (!fullName) return ""
    const parts = fullName.trim().split(" ")
    if (parts.length === 1) return parts[0]
    return `${parts[0]} ${parts[parts.length - 1][0]}.`
  }

  return (
    <div className="busy-technicians ">
      <div className="head">
        <h3>{trans("analytics.technicianBusynessRealtime")}</h3>
      </div>
      <hr />
      <div className="container">
        <div className="left">
          <div className="chart-heading">
            <span>{trans("analytics.currentBusyness")}</span>
            <b>{moment().format("DD.MM.YYYY")}</b>
          </div>
          <Gauge
            width={200}
            height={120}
            value={extraChartData.busyPercent || 0}
            startAngle={-90}
            endAngle={90}
            sx={(theme) => ({
              fill: "#181F67",
            })}
            text={({ value, valueMax }) => ""}
          />
          <div className="value">{`${extraChartData.busyPercent || 0}%`}</div>
          <table className="legend">
            <tbody>
              <tr>
                <td
                  style={{
                    width: "8px",
                    padding: 0,
                  }}
                >
                  <span
                    style={{
                      backgroundColor: "#181F67",
                    }}
                  ></span>
                </td>
                <td>{trans("analytics.busyTechnicians")}</td>
                <td
                  style={{
                    color: "#615E83",
                  }}
                >
                  {extraChartData.busy || 0}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    width: "8px",
                    padding: 0,
                  }}
                >
                  <span
                    style={{
                      backgroundColor: "#C6D2FD",
                    }}
                  ></span>
                </td>
                <td>{trans("analytics.freeTechnicians")}</td>
                <td
                  style={{
                    color: "#615E83",
                  }}
                >
                  {extraChartData.free || 0}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="right">
          {loading ? (
            <Skeleton count={5} height={30} style={{ marginBottom: 10 }} />
          ) : (
            <>
              <div className="body">
                <div className="schedule" ref={scheduleRef}>
                  <div className="chart-heading">{trans("analytics.schedule")}</div>
                  <table ref={tableRef}>
                    <tbody>
                      <tr
                        className={`with-hours ${busyTechnicians.length ? "busy-technicians" : "free-technicians"}`}
                      >
                        <td>
                          {busyTechnicians.length || freeTechnicians.length
                            ? busyTechnicians.length
                              ? trans("analytics.busy")
                              : trans("analytics.free")
                            : ""}
                        </td>
                        <td>
                          <ul className="hours">
                            {HOURS.map((hour) => (
                              <li
                                key={hour}
                                style={{
                                  minWidth: 60 * PX_PER_MINUTE,
                                  marginRight: 0,
                                }}
                              >
                                {hour}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                      {busyTechnicians.map((tech) => (
                        <tr key={tech.id}>
                          <td>
                            <div className="user">
                              <img src={tech.avatar} alt={tech.name} />
                              {getShortName(tech.name)}
                            </div>
                          </td>
                          <td>
                            <ul>
                              {(tech?.reservations || []).map((res) => {
                                const mStart = moment(
                                  res.start_date,
                                  "YYYY-MM-DD HH:mm:ss",
                                )
                                const mEnd = moment(
                                  res.end_date,
                                  "YYYY-MM-DD HH:mm:ss",
                                )
                                const left =
                                  (mStart.hour() * 60 + mStart.minute()) *
                                  PX_PER_MINUTE
                                const width = Math.max(
                                  mEnd.diff(mStart, "minutes") * PX_PER_MINUTE,
                                  PX_PER_MINUTE,
                                )
                                return (
                                  <li key={res.id}>
                                    <div
                                      className="reservation"
                                      style={{
                                        position: "absolute",
                                        left: left + 15,
                                        width: width + 10,
                                      }}
                                    >
                                      <b>{`#${res.reference_code}`}</b>
                                      <span>{res.appliances_count}</span>
                                    </div>
                                  </li>
                                )
                              })}
                            </ul>
                          </td>
                        </tr>
                      ))}
                      {busyTechnicians.length && freeTechnicians.length ? (
                        <tr className="free-technicians">
                          <td>{trans("analytics.free")}</td>
                          <td></td>
                        </tr>
                      ) : (
                        <></>
                      )}
                      {freeTechnicians.map((tech) => (
                        <tr key={tech.id}>
                          <td>
                            <div className="user">
                              <img src={tech.avatar} alt={tech.name} />
                              {getShortName(tech.name)}
                            </div>
                          </td>
                          <td>
                            <ul>
                              {(tech?.reservations || []).map((res) => {
                                const mStart = moment(
                                  res.start_date,
                                  "YYYY-MM-DD HH:mm:ss",
                                )
                                const mEnd = moment(
                                  res.end_date,
                                  "YYYY-MM-DD HH:mm:ss",
                                )
                                const left =
                                  (mStart.hour() * 60 + mStart.minute()) *
                                  PX_PER_MINUTE
                                const width = Math.max(
                                  mEnd.diff(mStart, "minutes") * PX_PER_MINUTE,
                                  PX_PER_MINUTE,
                                )
                                return (
                                  <li key={res.id}>
                                    <div
                                      className="reservation"
                                      style={{
                                        position: "absolute",
                                        left: left + 15,
                                        width: width + 10,
                                      }}
                                    >
                                      <b>{`#${res.reference_code}`}</b>
                                      <span>{res.appliances_count}</span>
                                    </div>
                                  </li>
                                )
                              })}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div
                    className="current-time"
                    style={{
                      left: currentTimeLeft,
                      height: tableHeight + 10 || "100%",
                    }}
                  >
                    <span></span>
                    <time>{currentTime}</time>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default BusyTechnicians
